import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { Calendar, ChevronRight, Clock, FileText } from "lucide-react";
import PageHero from "@/components/PageHero";
import { OPD_TIMINGS, DOCTORS } from "@/lib/data";
import { BookContext } from "@/components/Layout";
import { useLang } from "@/lib/i18n";

const OPD_SPECIALTIES = [
  "Ophthalmologist", "Orthopaedic Surgeon", "Physician",
  "ENT Surgeon", "Dermatologist", "Nephrologist",
  "Gastroenterologist", "Physiotherapist", "Dental Surgeon",
  "Radiologist", "Ultrasonologist", "Cardiologist",
];

export default function OPD() {
  const { open } = useContext(BookContext);
  const { t } = useLang();

  const reportTimings = [
    { label: t("opd.report.morning"), time: "09:00 AM – 1:00 PM" },
    { label: t("opd.report.evening"), time: "05:30 PM – 7:00 PM" },
  ];

  return (
    <div data-testid="opd-page">
      <PageHero
        eyebrow={t("opd.eyebrow")}
        title={t("opd.title")}
        subtitle={t("opd.subtitle")}
        crumbs={[{ label: t("nav.opd") }]}
        image="https://images.unsplash.com/photo-1586773860363-8ec8703e6aa5?w=1600&q=80"
      />

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-3xl border border-black/5 shadow-soft overflow-hidden" data-testid="opd-table">
            <div className="px-7 py-5 border-b border-black/5 flex items-center gap-3">
              <Clock className="w-5 h-5 text-brand" strokeWidth={1.7} />
              <h2 className="font-display text-xl text-deep font-semibold">{t("opd.weekly")}</h2>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {OPD_TIMINGS.map((row) => {
                  const dayLabel = t(`opd.day.${row.day}`);
                  const isClosed = row.time === "Closed";
                  return (
                    <tr key={row.day} className={`border-b border-black/5 last:border-0 ${isClosed ? "bg-red-50/40" : ""}`}>
                      <td className="px-7 py-4 font-display text-deep font-semibold text-base">{dayLabel}</td>
                      <td className={`px-7 py-4 text-right font-medium ${isClosed ? "text-red-700" : "text-ink-soft"}`}>
                        {isClosed ? t("opd.closed") : row.time}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="bg-deep text-white rounded-3xl p-7 shadow-lifted">
            <FileText className="w-7 h-7 text-gold mb-4" strokeWidth={1.5} />
            <h3 className="font-display text-xl font-semibold mb-2">{t("opd.reportCollection")}</h3>
            <p className="text-white/65 text-sm mb-5">{t("opd.monSat")}</p>
            <ul className="space-y-3">
              {reportTimings.map((r) => (
                <li key={r.label} className="flex items-center justify-between text-sm border-b border-white/10 pb-3 last:border-0">
                  <span className="text-white/70">{r.label}</span>
                  <span className="font-semibold">{r.time}</span>
                </li>
              ))}
            </ul>
            <button onClick={() => open()} className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-gold text-deep rounded-full font-semibold hover:bg-gold-600 transition-colors" data-testid="opd-book-btn">
              <Calendar className="w-4 h-4" /> {t("opd.bookOpd")}
            </button>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-brand/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <div className="text-[11px] tracking-[0.25em] uppercase text-gold font-bold mb-3">{t("opd.specialtyClinics.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-4xl text-deep font-semibold">{t("opd.specialtyClinics.title")}</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {OPD_SPECIALTIES.map((sp) => {
              const count = DOCTORS.filter((d) => d.specialty === sp).length;
              return (
                <Link
                  key={sp}
                  to={`/doctors?specialty=${encodeURIComponent(sp)}`}
                  className="group p-5 bg-white rounded-2xl border border-black/5 hover:border-brand hover:shadow-soft transition-all"
                  data-testid={`opd-spec-${sp.replace(/\s+/g, '-')}`}
                >
                  <div className="font-display text-deep font-semibold text-base group-hover:text-brand transition-colors">{sp}</div>
                  <div className="text-xs text-ink-soft mt-1.5">
                    {t(count === 1 ? "opd.doctorCount" : "opd.doctorCountPlural", { n: count })}
                  </div>
                  <ChevronRight className="w-4 h-4 mt-3 text-brand group-hover:translate-x-1 transition-transform" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
